import React from 'react'
import { observer } from 'mobx-react-lite'

import Avatar from '@mui/material/Avatar'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemAvatar from '@mui/material/ListItemAvatar'
import ListItemText from '@mui/material/ListItemText'

import { CategoryIcon, CategoryIconVariants, Icon, Text } from '@mxenabled/mxui'

import { useGlobalCopyStore, useTransactionStore } from '../../../../common'
import { DATE_FORMATS_INTL, formatDate } from '../../../../core/constants'
import { fromUnixUTC } from '../../../../core/utils/DateUtil'
import { type Transaction } from '../../../../core'

interface TransactionDetailsViewProps {
  transaction: Transaction
}

const renderItem = (
  icon: React.ReactNode,
  primary: string,
  secondary: React.ReactNode,
  show: boolean = true,
  dividerVariant: 'inset' | 'fullWidth' = 'inset',
) => {
  if (!show) return null
  return (
    <React.Fragment>
      <ListItem disableGutters={true} disablePadding={true}>
        <ListItemAvatar>
          <Avatar sx={{ bgcolor: 'neutral.light' }} variant="rounded">
            {icon}
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={primary}
          secondary={secondary}
          slotProps={{ primary: { variant: 'caption', fontSize: 11, fontWeight: 'normal' } }}
        />
      </ListItem>
      <Divider variant={dividerVariant} />
    </React.Fragment>
  )
}

const TransactionDetailsView = ({ transaction }: TransactionDetailsViewProps) => {
  const { transactions: copy } = useGlobalCopyStore()
  const { tags } = useTransactionStore()

  const tagNames = React.useMemo(() => {
    const tagMap = new Map(tags.map((tag) => [tag.guid, tag.name]))

    return transaction.tags
      .map((tagGuid) => tagMap.get(tagGuid))
      .filter(Boolean)
      .join(' / ')
  }, [tags, transaction.tags])

  return (
    <List sx={{ '& .MuiListItem-root': { px: 24, py: 12 } }}>
      {renderItem(
        <CategoryIcon
          categoryGuid={transaction.top_level_category_guid}
          size={24}
          sx={{ color: 'text.primary' }}
          variant={CategoryIconVariants.Transparent}
        />,
        copy.category_title,
        <Text bold={true} variant="body1">
          {transaction.category ?? copy.actions_select_a_category}
        </Text>,
      )}

      {renderItem(
        <Icon name="calendar_month" sx={{ color: 'text.primary' }} />,
        copy.date_title,
        <Text bold={true} variant="body1">
          {formatDate(fromUnixUTC(transaction.date), DATE_FORMATS_INTL.FULL_MONTH_DAY_YEAR)}
        </Text>,
      )}

      {renderItem(
        <Icon name="article" sx={{ color: 'text.primary' }} />,
        copy.memo_title,
        <Text bold={true} variant="body1">
          {transaction.memo ?? copy.memo_desc}
        </Text>,
        Boolean(transaction.memo),
        transaction.tags?.length > 0 ? 'inset' : 'fullWidth',
      )}

      {renderItem(
        <Icon name="local_offer" sx={{ color: 'text.primary' }} />,
        copy.tags_title,
        <Text bold={true} variant="body1">
          {transaction.tags?.length ? tagNames : copy.tags_desc}
        </Text>,
        Boolean(transaction.tags?.length),
        'fullWidth',
      )}
    </List>
  )
}

export default observer(TransactionDetailsView)
